/* eslint-disable */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { filter, finalize, first, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators'
import { forkJoin, merge, Observable, of, Subject } from 'rxjs'

import { User, UserStatus } from '@shared/models/user.model'
import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { Role } from '@shared/models/role.model'
import { AuthService } from '@app/modules/auth/auth.service'
import { RoleService } from '@shared/services/role.service'
import { PermissionsService } from '@shared/services/permissions.service'
import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { DialogService } from '@shared/dialogs/dialog.service'
import { RoleType } from '@core/types/role.type'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { Client } from '@shared/models/client.model'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { UserGroup } from '@shared/models/user-group.model'
import { UserGroupsService } from '@app/modules/client/config/user-groups/user-groups.service'
import { UtilityService } from '@core/services/utility.service'
import { Team, TeamType } from '@shared/models/team.model'
import { DestroyService } from '@core/services/destroy.service'
import { PermissionSourceType, PermissionsSource } from '@core/types/permissions.source'
import { DriverRouteScopeLabel } from '@shared/models/driver.model'
import { Timezone } from '@app/shared/models/timezone.model'
import { DataService } from '@app/shared/services/data.service'
import dayjs from 'dayjs'
import { SettingsService } from '../settings/settings.service'
import { SettingName } from '@app/shared/models/setting.model'

@Component({
  selector: 'lin-update-user-form',
  templateUrl: './update-user-form.component.html',
  styleUrls: ['./update-user-form.component.scss'],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UpdateUserFormComponent implements OnInit, OnDestroy, TrackedUnsavedForm {
  userForm: FormGroup
  roles: Role[]
  formError: string
  avatar: string | ArrayBuffer
  private avatarField: FileList
  currentUser: User
  timezones: Timezone[]
  isDisabled = false
  isLoaded = false
  isSubmitting = false
  isPasswordResetting = false
  isFormChanged: ReturnType<typeof takeFormSnapshot>
  DriverRouteScopeLabel = DriverRouteScopeLabel
  private blockoutEnabled = false

  @Input() user: User
  @Input() client: Client
  @Input() userGroups: UserGroup[]

  private readonly onDestroy$ = new Subject<void>()
  private currentTimezone: number | null = null

  readonly PermissionsSource = PermissionsSource
  private _isEditMode = false
  private _isResetPassword = false
  private _isBlockedMode = false
  private userTeam: Team
  private keyContactId: string | null = null

  get isDriver(): boolean {
    return this.user.role.name === RoleType.Driver
  }

  get isKeyContact(): boolean {
    return this.keyContactId != null && this.keyContactId === String(this.user.id)
  }

  get isAdminTeam(): boolean {
    return this.currentUser.teamType === TeamType.Admin
  }

  get isEditMode(): boolean {
    return this._isEditMode
  }

  get isResetPasswordMode(): boolean {
    return this._isResetPassword
  }

  get isBlockedMode(): boolean {
    return this._isBlockedMode
  }

  get canBeDualAccount(): boolean {
    const roleId = +this.userForm?.get('roleId')?.value
    return roleId === 6 || roleId === 7
  }

  get hasDriverFields(): boolean {
    return this.userForm?.get('roleId')?.value === 8 || (this.canBeDualAccount && <boolean>this.userForm?.get('isDualAccount')?.value)
  }

  get isSSOUser(): boolean {
    return this.user.isSSO
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly clientsService: ClientsService,
    private readonly translateService: TranslateService,
    private readonly dialog: DialogService,
    private readonly authService: AuthService,
    private readonly roleService: RoleService,
    private readonly permissionsService: PermissionsService,
    private readonly unsavedFormTrackerService: UnsavedFormTracker,
    private readonly userGroupsService: UserGroupsService,
    private readonly utilityService: UtilityService,
    private readonly dataService: DataService,
    private readonly destroyService: DestroyService,
    private readonly settingsService: SettingsService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.unsavedFormTrackerService.track(this)
    this.initData()
  }

  private initData(): void {
    const userGroups$: Observable<UserGroup[]> = this.userGroupsService.getUserGroupList()
    const inputUserGroups$: Observable<UserGroup[]> = this.userGroups ? of(this.userGroups) : userGroups$
    const timezones$ = this.dataService.getTimezones()

    forkJoin([inputUserGroups$, timezones$])
      .pipe(
        tap(([userGroups, timezones]) => {
          this.userGroups = userGroups
          this.timezones = timezones
        }),
        switchMap(() => this.roleService.clientTeamRoles$),
        tap((roles: Role[]) => (this.roles = roles)),
        switchMap(() => this.initUser()),
        switchMap(() =>
          this.settingsService.getSettings(this.currentUser.team.id).pipe(
            tap((settings) => {
              this.blockoutEnabled = !!settings.find((s) => s.name === SettingName.Blockout)?.value
            }),
          ),
        ),
        takeUntil(this.destroyService),
      )
      .subscribe(() => {
        this._isEditMode = this.getMode('EDIT')
        this._isResetPassword = this.getMode('RESET_PASSWORD')
        this._isBlockedMode = this.getMode('BLOCK_USER')

        this.initForm()
        this.setFormValues()
        this.disableFormControls()

        const currentTeamType = this.currentUser.role.team
        const currentRoleDisplay = this.currentUser.role.displayName

        // Prevent Drivers/Managers from changing timezone if Blockout is ON
        if (this.blockoutEnabled && currentTeamType === 'client' && (currentRoleDisplay === 'Driver' || currentRoleDisplay === 'Manager')) {
          this.userForm
            .get('timezone')
            ?.valueChanges.pipe(takeUntil(this.onDestroy$))
            .subscribe((newValue) => {
              if (newValue !== this.currentTimezone) {
                if (this.isOutsideBlockoutTime()) {
                  this.revertTimezoneWithDialog()
                } else {
                  this.currentTimezone = newValue
                }
              }
            })
        }

        this.isLoaded = true
        this.cdr.markForCheck()
      })
  }

  private revertTimezoneWithDialog(): void {
    this.userForm.get('timezone')?.setValue(this.currentTimezone, { emitEvent: false })

    this.dialog
      .info(
        this.translateService.instant('DIALOGS.TITLES.TIMEZONE_CHANGE_NOT_ALLOWED'),
        this.translateService.instant('DIALOGS.MESSAGES.CONTACT_ADMIN_CHANGE_TIMEZONE'),
      )
      .afterClosed()
      .pipe(takeUntil(this.destroyService))
      .subscribe(() => {
        this.userForm.get('timezone')?.setValue(this.currentTimezone, { emitEvent: false })
      })
  }

  private isOutsideBlockoutTime(): boolean {
    const now = new Date()
    const hour = now.getHours()
    const minute = now.getMinutes()

    // Block before 9:00
    if (hour < 9) return true
    if (hour === 9 && minute < 0) return true

    // Block after 5:00 PM
    if (hour > 17) return true
    if (hour === 17 && minute >= 0) return true

    return false
  }

  private initUser(): Observable<User> {
    const id = (this.route.parent.snapshot.params.id as string) || this.user.id

    const user$ = this.authService.currentUser$.pipe(filter(Boolean), first(), shareReplay())

    const adminTeam$: Observable<User> = user$.pipe(
      filter((user: User) => user.isAdminOrReseller()),
      tap((user) => {
        this.currentUser = user
        this.userTeam = {
          clientId: this.client.id,
          id: user.team.id,
          type: TeamType.Admin,
          clientName: this.client.name,
        }
        this.keyContactId = user.keyContactId != null ? String(user.keyContactId) : null
      }),
    )

    const clientTeam$: Observable<User> = user$.pipe(
      filter((user: User) => user.isClient()),
      tap((user) => {
        this.currentUser = user
        this.userTeam = user.team
        this.keyContactId = user.keyContactId != null ? String(user.keyContactId) : null
      }),
      switchMap(() => this.clientsService.getClientUser(this.userTeam.clientId, id) as Observable<User>),
      tap((user) => (this.user = user)),
    )

    return merge(clientTeam$, adminTeam$)
  }

  hasPermission(permissionType: PermissionSourceType): boolean {
    return this.permissionsService.hasPermissionSync(permissionType)
  }

  private patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    /* eslint-disable */
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null
      }

      return regex.test(control.value) ? null : error
    }
    /* eslint-enable */
  }

  private initForm(): void {
    const { role } = this.currentUser
    const isEmailEditEnabled =
      role.team === TeamType.Admin ||
      (TeamType.Reseller && (role.name === RoleType.Admin || role.name === RoleType.SuperAdmin || role.name === RoleType.ResellerAdmin))

    this.userForm = this.formBuilder.group({
      name: ['', Validators.required],
      surname: ['', Validators.required],
      email: [{ value: '', disabled: !isEmailEditEnabled }],
      roleId: ['', Validators.required],
      isDualAccount: false,
      driverRouteScope: '',
      groups: '',
      driverId: '',
      driverFOBId: '',
      driverSensorId: '',
      phone: ['', [Validators.required, Validators.minLength(8), this.patternValidator(/^\+(?:[\d]*)$/, { phoneNumber: true })]],
      position: '',
      status: [{ value: '', disabled: true }],
      blockingMessage: [''],
      language: '',
      timezone: '',
    })
    this.isBlockedMode && this.userForm.addControl('isBlocked', this.formBuilder.control(false))

    this.userForm.valueChanges.pipe(takeUntil(this.destroyService)).subscribe(() => this.onFormValuesChanged())
    this.listenDriverIdValueChanges()
  }

  private listenDriverIdValueChanges(): void {
    this.userForm.controls.driverId.valueChanges
      .pipe(
        filter(Boolean),
        takeUntil(this.destroyService),
        switchMap((driverId: string) => this.clientsService.checkDriverId(this.userTeam.id, this.user.id, driverId)),
      )
      .subscribe((isUniqueDriverId) => {
        const error: { [key: string]: boolean } | null = isUniqueDriverId ? null : { existingName: true }

        this.userForm.controls.driverId.setErrors(error)
        this.userForm.controls.driverId.markAsTouched()
        this.cdr.markForCheck()
      })
  }

  private setFormValues(): void {
    const { user } = this

    const toId = ({ id }: UserGroup) => id

    this.userForm.patchValue({
      name: user.name,
      email: user.email,
      surname: user.surname,
      roleId: user.role.id,
      isDualAccount: user.isDualAccount,
      driverRouteScope: user.driverRouteScope || '',
      phone: user.phone,
      groups: user.userGroups.map(toId),
      position: user.position,
      status: user.status,
      driverId: user.driverId,
      language: user.language,
      driverFOBId: user.driverFobId,
      driverSensorId: user.driverSensorId,
      timezone: user.timezone?.id,
      ...(this.isBlockedMode && { isBlocked: user.status === UserStatus.Blocked }),
    })

    this.currentTimezone = user.timezone?.id // ✅ store original timezone
    this.isFormChanged = takeFormSnapshot(this.userForm)
  }

  private disableFormControls(): void {
    const { role, id } = this.currentUser
    const currentUserIdStr = id != null ? String(id) : null
    const editedUserIdStr = this.user.id != null ? String(this.user.id) : null
    const isSelfEdit = currentUserIdStr != null && currentUserIdStr === editedUserIdStr
    const isCurrentUserManager = role.name === RoleType.Manager
    const isCurrentUserDriver = role.name === RoleType.Driver
    const isCurrentUserAdmin = role.name === RoleType.Admin
    const isCurrentUserSuperAdmin = role.name === RoleType.SuperAdmin
    const isEditedUserNotDriver = isCurrentUserManager && this.user.role.name !== RoleType.Driver

    if ((isCurrentUserDriver || isEditedUserNotDriver) && !isSelfEdit) {
      this.userForm.disable()
      this.isDisabled = true
    }

    if (!this.currentUser.isReseller() && ((isCurrentUserAdmin && isSelfEdit) || !(isCurrentUserAdmin || isCurrentUserSuperAdmin))) {
      this.userForm.controls.isBlocked?.disable()
      this.userForm.controls.roleId?.disable()
      this.userForm.controls.isDualAccount?.disable()
      this.userForm.controls.driverRouteScope?.disable()
      this.userForm.controls.name?.disable()
      this.userForm.controls.surname?.disable()
    }

    if (this.isKeyContact) {
      this.userForm.controls.isBlocked?.disable()
    }

    !this.isEditMode && this.userForm.disable()
  }

  private onFormValuesChanged(): void {
    this.formError = ''
  }

  saveData(): void {
    this.userForm.controls.isBlocked.value && this.isKeyContact
      ? this.dialog.error(<string>this.translateService.instant('DIALOGS.MESSAGES.KEY_CONTACT_CAN_NOT_BE_DELETED')).afterClosed()
      : this.updateClientUser()
  }

  private updateClientUser(): void {
    this.isSubmitting = true
    const saveData: Partial<User> = this.prepareDataForSave()
    const fieldsSkipValueChecking = saveData['groups'] ? null : ['groups']

    this.clientsService
      .updateClientUser(this.userTeam.clientId, +this.user.id, saveData, fieldsSkipValueChecking)
      .pipe(
        finalize(() => {
          this.isSubmitting = false
          this.cdr.markForCheck()
        }),
      )
      .subscribe(() => {
        this.isFormChanged = takeFormSnapshot(this.userForm)
        if (this.currentUser.isClient()) {
          void this.router.navigate(['../..'], { relativeTo: this.route })
        } else {
          void this.router.navigate([this.currentUser.teamType, 'clients', this.userTeam.clientId, 'users'])
        }
      }, handleFormErrors(this.userForm))
  }

  uploadAvatar(file: FileList): void {
    if (file && file.length) {
      const reader: FileReader = new FileReader()
      this.avatarField = file

      reader.readAsDataURL(file[0])
      reader.onload = (event) => {
        this.avatar = event.target.result

        this.userForm.markAsDirty()
        this.cdr.markForCheck()
      }
    }
  }

  private prepareDataForSave(): Partial<User> {
    /* eslint-disable */
    const value = this.userForm.value
    const data = {
      ...value,
      ...(this.avatarField && this.avatarField[0] && { avatar: this.avatarField[0] }),
      driverId: this.hasDriverFields ? value.driverId : null,
      driverFOBId: value.driverFOBId,
      driverSensorId: this.hasDriverFields ? this.utilityService.macAddressToString(value.driverSensorId) : null,
      groups: value.groups?.length ? value.groups : null,
      ...(value.hasOwnProperty('isDualAccount') && this.canBeDualAccount && { isDualAccount: value.isDualAccount }),
    }
    return data
    /* eslint-enable */
  }

  resetPassword(): void {
    this.dialog
      .confirm(<string>this.translateService.instant('DIALOGS.RESET_PASSWORD'))
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => {
          this.isPasswordResetting = true
          this.cdr.markForCheck()
        }),
        switchMap(() => this.authService.requestNewPassword(this.user.email, true)),
        finalize(() => {
          this.isPasswordResetting = false
          this.cdr.markForCheck()
        }),
      )
      .subscribe(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        (error) => {
          this.formError = handleFormErrors(this.userForm)(error)
          this.cdr.markForCheck()
        },
      )
  }

  ngOnDestroy(): void {
    this.unsavedFormTrackerService.discard(this)
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private getMode(action: 'EDIT' | 'RESET_PASSWORD' | 'BLOCK_USER'): boolean {
    if (this.isAdminTeam) return true

    const isDriverPath = location.pathname.match('drivers')

    return this.isDriver && isDriverPath
      ? this.hasPermission(PermissionsSource[`DRIVERS.DRIVERS_LIST.${action}`])
      : this.hasPermission(PermissionsSource[`CONFIGURATION.USERS_LIST.${action}`])
  }
}
